/**
 * Repoints every reference to a queued attachment's temporary id (`att-pending:<uuid>`, see
 * FileUploadService.queueAttachmentUpload) at its server id. Covers both the raw id (e.g.
 * `data-attachment-id`) and its URL-encoded form (e.g. the `/api/attachments/<id>/file` src).
 */
export function replaceAttachmentIdInHtml(html: string | null | undefined, oldId: string, newId: string): string {
  const source = String(html ?? '');
  if (!source || !oldId || oldId === newId) {
    return source;
  }
  const encodedOld = encodeURIComponent(oldId);
  let result = source.split(oldId).join(newId);
  if (encodedOld !== oldId) {
    result = result.split(encodedOld).join(encodeURIComponent(newId));
  }
  return result;
}

const parseFragment = (html: string): HTMLTemplateElement => {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template;
};

const attachmentImageSelector = 'img[data-image-source-type="attachment"][data-attachment-id]';

/** Ids of the attachments shown as inline images in a note's rich text, in document order, deduplicated. */
export function extractAttachmentImageIds(html: string | null | undefined): string[] {
  const source = String(html ?? '');
  if (!source.includes('data-attachment-id')) {
    return [];
  }
  const ids: string[] = [];
  parseFragment(source).content.querySelectorAll(attachmentImageSelector).forEach((img) => {
    const id = String(img.getAttribute('data-attachment-id') ?? '').trim();
    if (id && !ids.includes(id)) {
      ids.push(id);
    }
  });
  return ids;
}

/** Removes every inline image showing the given attachment (used when the attachment is deleted). */
export function removeAttachmentImagesFromHtml(html: string | null | undefined, attachmentId: string): string {
  const source = String(html ?? '');
  if (!attachmentId || !source.includes(attachmentId)) {
    return source;
  }
  const template = parseFragment(source);
  let removed = false;
  template.content.querySelectorAll(attachmentImageSelector).forEach((img) => {
    if (img.getAttribute('data-attachment-id') === attachmentId) {
      img.remove();
      removed = true;
    }
  });
  return removed ? template.innerHTML : source;
}

/** True when any of the given HTML strings refers to the attachment: an inline image, a link to its
 * file, or any other use of its id (raw or URL-encoded). */
export function isAttachmentReferencedInHtml(attachmentId: string, ...htmls: Array<string | null | undefined>): boolean {
  if (!attachmentId) return false;
  const encoded = encodeURIComponent(attachmentId);
  return htmls.some((html) => {
    const source = String(html ?? '');
    return source.includes(attachmentId) || (encoded !== attachmentId && source.includes(encoded));
  });
}
